import wcs from '@/scripts/libs/wcs'
import {
  AlgoliaPrice,
  AlgoliaProductHit,
  Attachment,
  StatusMessageTypeE,
} from '@/scripts/libs/algolia/types/plp'
import { WcsBadgeBZone } from '@/scripts/interfaces/normalizer'
import { WcsStoreId } from '@/scripts/libs/wcs/types/store-id'
import { formatPrice } from '@/scripts/libs/price'
import { FLASH, PST, SCARCITY } from '@/scripts/constants/promos'
import { Promotion } from '@/types/promotions'
import { NEGATIVE_VALUES, ONE, POSITIVE_VALUES, TRUE, ZERO } from '@/scripts/constants/booleans'
import { AttributesPriorityD, PlpColor, PlpProduct } from '@/types/plp'
import {
  ALL_ELECTRONICS_PRODUCT_TYPES,
  CLIPON,
  ELECTRONICS,
  KIT,
  PRIVATE,
  ROX_ELECTRONICS,
  SEMIPRIVATE,
  AVAILABLE_ANALYTICS,
} from '@/scripts/constants/pdp'
import { AlgoliaTracking } from '@/scripts/libs/algolia/types/analytics'
import { GIFTCARDS } from '@/scripts/constants/store'
import isEmpty from 'lodash/isEmpty'
import { ALT_IMAGE_TEXT } from '@/scripts/libs/products'

class AlgoliaProductsUtils {
  private getPlpTranslation = () => {
    const plpTranslationsEls = document.querySelectorAll<HTMLElement>('#data_plp_labels')
    if (!plpTranslationsEls) {
      return {}
    }

    const plpTranslations = [...plpTranslationsEls]

    let translations: any = {}

    plpTranslations.forEach((translation) => {
      translations = {
        ...translations,
        ...JSON.parse(translation?.innerText).plp,
      }
    })

    return translations
  }

  private mapBadgeBZone = (product: AlgoliaProductHit): WcsBadgeBZone | undefined => {
    const { storeId } = wcs.getStoreConfig()
    const plpTranslations = this.getPlpTranslation()

    const { attributes_translated, inventoryQuantity } = product

    if (inventoryQuantity < 0) {
      return undefined
    }

    if (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.PREORDER))) {
      return {
        label: plpTranslations?.preorder,
        style: 'preorder',
      }
    } else if (
      inventoryQuantity === 0 &&
      (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.EXCLUSIVE)) ||
        POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.LIMITED_EDITION)))
    ) {
      return {
        label: `${plpTranslations?.soldOut}`.toLocaleUpperCase(),
        style: 'sold-out',
      }
    } else if (
      POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.NEWRELEASES))
    ) {
      return {
        label: plpTranslations?.newLabel,
        style: 'new',
      }
    } else if (
      this.getAttributeValue(attributes_translated?.LIMITED_EDITION) &&
      !NEGATIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.LIMITED_EDITION))
    ) {
      return {
        label: 'Limited',
        style: 'limited',
      }
    } else if (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.EXCLUSIVE))) {
      if (storeId === WcsStoreId.JAPAN) {
        return {
          label: 'オンライン限定',
          style: 'web-exclusive',
        }
      } else {
        return {
          label: plpTranslations?.webExclusive,
          style: 'web-exclusive',
        }
      }
    }
    return undefined
  }

  private mapBadgeGZone = (product: AlgoliaProductHit): string => {
    const plpTranslations = this.getPlpTranslation()

    const { attributes_translated } = product

    if (this.getAttributeValue(attributes_translated?.ONSALE) === 'SEMIPRIVATE') {
      return 'Member Access'
    }

    if (
      this.getAttributeValue(attributes_translated?.PRESCRIPTION_AVAILABLE) === 'Y' &&
      WcsStoreId.currentCountryHasRx()
    ) {
      return plpTranslations.prescriptionAvailable
    }

    if (this.getAttributeValue(attributes_translated?.PRESCRIPTION_AVAILABLE) === 'Children') {
      return plpTranslations.junior
    }

    return ''
  }

  private mapPromotion = (name: string, algoliaPrice: AlgoliaPrice): Promotion => {
    const {
      currencySymbol,
      plp: { priceFormat },
    } = wcs.getStoreConfig()

    const { badge = '', endDate, startDate, offerPrice, precedence, segment, toggle } = algoliaPrice
    let type = ''
    if (name.toLowerCase().includes(PST)) {
      type = PST
    } else if (name.toLowerCase().includes(FLASH)) {
      type = SCARCITY
    }

    const sanitizedStartDate = `${startDate.slice(0, -2)} CDT`
    const sanitizedEndDate = `${endDate.slice(0, -2)} CDT`

    return {
      badge,
      toggle,
      endDate: sanitizedEndDate,
      price: String(offerPrice),
      priceLabel: formatPrice(offerPrice, priceFormat, currencySymbol),
      priority: String(precedence),
      startDate: sanitizedStartDate,
      type,
      unformattedAmount: String(offerPrice),
      userSegments: segment?.split(',') ?? [],
    }
  }

  private mapOriginalPrice = (product: AlgoliaProductHit): string => {
    const { prices } = product
    if (!prices || isEmpty(prices)) return ''

    const {
      currencySymbol,
      plp: { priceFormat },
    } = wcs.getStoreConfig()

    const validPrices = Object.values(prices)
      .filter((promotion) => promotion.precedence === 0 && !promotion.badge)
      .sort((priceA, priceB) => priceB.priceListPrecedence - priceA.priceListPrecedence)

    return formatPrice(validPrices[0]?.listPrice, priceFormat, currencySymbol)
  }

  private mapPromotions = (product: AlgoliaProductHit): Array<Promotion> => {
    const { prices } = product

    if (!prices || isEmpty(prices)) {
      return []
    }

    const validPromotions = Object.entries(prices).filter(
      ([, promotion]) => promotion.percentageDiscount || promotion.amountOfDiscount
    )
    const promotions = validPromotions.map(([name, promotion]) =>
      this.mapPromotion(name, promotion)
    )

    const validFuturePromotions = Object.entries(prices).filter(
      ([, promotion]) => promotion.futurePrices
    )
    const futurePromotions = validFuturePromotions
      .map(([name, promotion]) => {
        if (!promotion.futurePrices) {
          return []
        }

        return promotion.futurePrices?.map((futurePrice) => this.mapPromotion(name, futurePrice))
      })
      .flatMap((item) => item)
    return [...promotions, ...futurePromotions]
  }

  private mapModelName(product: AlgoliaProductHit): string {
    const { attributes_translated } = product

    if (!attributes_translated?.MODEL_NAME) {
      return ''
    }

    if (
      attributes_translated?.PRODUCT_TYPE &&
      ALL_ELECTRONICS_PRODUCT_TYPES.includes(attributes_translated?.PRODUCT_TYPE)
    ) {
      return Array.isArray(attributes_translated?.MODEL_NAME)
        ? attributes_translated?.MODEL_NAME[0]
        : attributes_translated?.MODEL_NAME
    }

    if (Array.isArray(attributes_translated?.MODEL_NAME)) {
      return attributes_translated?.MODEL_NAME.join(', ')
    }

    return this.getAttributeValue(attributes_translated?.MODEL_NAME)
  }

  public normalizeProduct = ({
    product,
    algoliaTracking,
  }: {
    product: AlgoliaProductHit
    algoliaTracking: AlgoliaTracking
  }): PlpProduct => {
    return {
      partNumber: product.partnumberId,
      catEntryId: String(product.productId),
      category: this.getAttributeValue(product.attributes_translated?.PRODUCT_TYPE) ?? '',
      colors: product.variants?.length
        ? product.variants?.map((variant, index) =>
            this.normalizeColor({ product: variant, index, algoliaTracking })
          )
        : [this.normalizeColor({ product, index: 0, algoliaTracking })],
      upc: product.partnumberId,
      sku: this.getAttributeValue(product.attributes?.SKU),
    }
  }

  private normalizeColor = ({
    product,
    index,
    algoliaTracking,
  }: {
    product: AlgoliaProductHit
    index: number
    algoliaTracking: AlgoliaTracking
  }): PlpColor => {
    const { countryPrefix } = wcs.getStoreConfig()
    const algoliaTrackingForColor = {
      ...algoliaTracking,
      ungrouped: {
        ...algoliaTracking.ungrouped,
        position: index,
      },
    }
    return {
      algoliaTracking: algoliaTrackingForColor,
      catEntryId: String(product.productId),
      preorder: this.getAttributeValue(product.attributes_translated?.PREORDER) === TRUE,
      electronics:
        this.getAttributeValue(product.attributes_translated?.PRODUCT_TYPE) === ELECTRONICS ||
        this.getAttributeValue(product.attributes_translated?.PRODUCT_TYPE) === ROX_ELECTRONICS,
      name: this.mapModelName(product),
      price: !wcs.isKorea() ? this.mapOriginalPrice(product) : '',
      partNumber: product.partnumberId,
      url: `${countryPrefix}${product.url}`,
      onSale:
        this.getAttributeValue(product.attributes_translated?.ONSALE) === PRIVATE ||
        this.getAttributeValue(product.attributes_translated?.ONSALE) === SEMIPRIVATE,
      images: {
        primary: this.getProductImage(product),
        secondary: this.getProductImageHover(product),
      },
      promotions: !wcs.isKorea() ? this.mapPromotions(product) : [],
      polarized: this.getAttributeValue(product.attributes_translated?.POLARIZED) === TRUE,
      customizable: this.getRemixable(product) ? this.getRemixableUrl(product) : '',
      sustainable: product.categories?.includes('collection_sustainable'),
      statusMessage: this.mapStatusMessage(product),
      attributesPriority: this.mapAttributes(product),
      attachments: product?.attachments || [],
      inventoryQuantity: product?.inventoryQuantity,
      frameType: product.attributes_translated?.FRAMETYPE,
      status: product.buyable ? AVAILABLE_ANALYTICS : '',
      moCo: product.parentPartnumberId,
      VmIsUpcSupported: product.attributes_translated?.VIRTUALMIRROR ? ONE : ZERO,
      product_type: product.attributes_translated?.PRODUCT_TYPE,
      imageAltText: this.buildProductAltText(product),
      isTransition: this.getIsTransition(
        this.getAttributeValue(product.attributes_translated.LENS_TREATMENT)
      ),
    }
  }

  private getIsTransition(label: string): boolean {
    return new RegExp(/TRANSITIONS/gi).test(label)
  }

  private mapAttributes(product: AlgoliaProductHit): Array<AttributesPriorityD> {
    const isTransition = this.getIsTransition(
      this.getAttributeValue(product.attributes_translated.LENS_TREATMENT)
    )
    const priority = [
      {
        type: 'sustainable',
        value: product.categories?.includes('collection_sustainable'),
        icon: '',
        label: 'common.product_tile.attributes.alternative_materials',
      },
      {
        type: 'polarized',
        value: this.getAttributeValue(product.attributes_translated?.POLARIZED) === TRUE,
        icon: 'sunglasses-polarized-black',
        label: 'common.product_tile.attributes.polarized',
      },
      {
        type: 'transitions',
        value: product.attributes?.MODEL_NAME?.includes('TRANSITIONS') || isTransition,
        icon: 'transitions-lens',
        label: 'common.product_tile.attributes.transitions',
      },
    ]
    return priority.filter((el: AttributesPriorityD) => el.value)
  }

  private getRemixable(product: AlgoliaProductHit): boolean {
    const isRemixable =
      !!(
        this.getAttributeValue(product.attributes_translated?.REMIXABLE) &&
        this.getAttributeValue(product.attributes_translated?.REMIXABLE) !== 'NONREMIXABLE'
      ) ||
      !!(
        this.getAttributeValue(product.attributes_translated?.CUSTOMIZABLE) &&
        POSITIVE_VALUES.includes(
          this.getAttributeValue(product.attributes_translated?.CUSTOMIZABLE)
        )
      )

    return isRemixable
  }

  private getProductImage(product: AlgoliaProductHit) {
    if (!product.attachments && product.media) {
      if (
        this.getAttributeValue(product.attributes_translated?.PRODUCTTYPE) &&
        [...GIFTCARDS, KIT].includes(
          this.getAttributeValue(product.attributes_translated?.PRODUCTTYPE)
        )
      ) {
        return product.media?.defaultImage2
      }
      return product.media?.defaultImage1
    }

    const plpImages = this.getPlpImages(product)
    if (!plpImages) {
      return ''
    }
    if (
      this.getAttributeValue(product.attributes_translated?.PRODUCTTYPE) &&
      [...GIFTCARDS, KIT].includes(
        this.getAttributeValue(product.attributes_translated?.PRODUCTTYPE)
      )
    ) {
      return this.getProductImageHover(product)
    }
    return plpImages[0]?.url
  }

  private getProductImageHover(product: AlgoliaProductHit) {
    if (!product.attachments && product.media) {
      return product.media?.defaultImage2
    }
    const plpImages = this.getPlpImages(product)
    if (!plpImages) {
      return ''
    }
    const plpImage = plpImages[1] ?? plpImages[0]
    return plpImage?.url
  }

  private getPlpImages(product: AlgoliaProductHit) {
    const plpImages = product.attachments?.filter((attachment) => attachment.rule === 'PLP')
    const sortedPlpImages = plpImages?.sort(
      (a: Attachment, b: Attachment) => a.sequence - b.sequence
    )
    return sortedPlpImages?.map((image) => ({ ...image, url: this.composeProductImage(image.url) }))
  }

  public composeProductImage(url: string) {
    const { assetMiddleLayerImagesHost } = wcs.getStoreConfig()
    return `${assetMiddleLayerImagesHost}${url}`
  }

  private getRemixableUrl(product: AlgoliaProductHit | undefined) {
    if (!product) {
      return ''
    }
    const { countryPrefix } = wcs.getStoreConfig()
    return product.remixableUrl
      ? `${countryPrefix}${product.remixableUrl}&upc=${product.partnumberId}`
      : ''
  }

  private getAttributeValue(attribute: string | string[] | undefined) {
    if (!attribute) {
      return ''
    }
    if (Array.isArray(attribute)) {
      return attribute[0] ?? ''
    }
    return attribute?.split('|')?.[0]
  }

  private formatAndCheckAvantPremierDate(date: string): boolean {
    // Parsing della data in formato DD-MM-YYYY oppure DD/MM/YYYY
    const divider = date.indexOf('/') > -1 ? '/' : '-'
    const [day, month, year] = date.split(divider).map(Number)
    const parsedDate = new Date(year, month - 1, day) // Mese è 0-indexed in JavaScript

    // Ottieni la data corrente (senza tempo per il confronto)
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    // Verifica se la data corrente è dopo quella fornita
    return today > parsedDate
  }

  private mapStatusMessage = (
    product: AlgoliaProductHit
  ): { label: string; type: StatusMessageTypeE } | undefined => {
    const { storeId } = wcs.getStoreConfig()
    const { attributes_translated, inventoryQuantity } = product
    if (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.PREORDER))) {
      return {
        label: 'common.product_tile.status_message.pre_order',
        type: StatusMessageTypeE.PREORDER,
      }
    } else if (
      POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.AVANTPREMIER)) &&
      !this.formatAndCheckAvantPremierDate(
        this.getAttributeValue(attributes_translated?.AVANTPREMIER_DATE)
      )
    ) {
      return {
        label: 'common.product_tile.status_message.pre_release',
        type: StatusMessageTypeE.PRERELEASE,
      }
    } else if (
      inventoryQuantity <= 0 &&
      (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.EXCLUSIVE)) ||
        POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.LIMITED_EDITION)))
    ) {
      return {
        label: 'common.product_tile.status_message.sold_out',
        type: StatusMessageTypeE.SOLDOUT,
      }
    } else if (
      this.getAttributeValue(attributes_translated?.LIMITED_EDITION) &&
      !NEGATIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.LIMITED_EDITION))
    ) {
      return {
        label: 'common.product_tile.status_message.limited_edition',
        type: StatusMessageTypeE.LIMITED,
      }
    } else if (POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.EXCLUSIVE))) {
      if (storeId === WcsStoreId.JAPAN) {
        return {
          label: 'オンライン限定',
          type: StatusMessageTypeE.WEBEXCLUSIVEJP,
        }
      } else {
        return {
          label: 'common.product_tile.status_message.web_exclusive',
          type: StatusMessageTypeE.WEBEXCLUSIVE,
        }
      }
    } else if (
      POSITIVE_VALUES.includes(this.getAttributeValue(attributes_translated?.NEWRELEASES))
    ) {
      return {
        label: 'common.product_tile.status_message.is_new',
        type: StatusMessageTypeE.NEW,
      }
    }
    return undefined
  }
  private buildProductAltText(product: AlgoliaProductHit): string {
    if (!product) {
      return ALT_IMAGE_TEXT
    }

    const {
      attributes: { ML_PRODUCT_TYPE, MODEL_NAME, FRONT_COLOR, LENS_COLOR, LENS_TREATMENT },
    } = product
    return `Ray-Ban ${ML_PRODUCT_TYPE} ${MODEL_NAME} ${FRONT_COLOR} with ${LENS_COLOR} ${LENS_TREATMENT} lens`
  }
}

export default new AlgoliaProductsUtils()
